/*
 * EnergyFlowRenderer — Sankey chart for heat-pump energy flow.
 *
 * Concept §3 calls this out as an HVAC-native visualisation: Strom →
 * Kompressor → Umwelt + Nutzwärme. Each FlowLink carries a live ValuePoint
 * that drives the link thickness at runtime. An optional CopPoint drives a
 * big number overlay top-right.
 *
 * ECharts owns the chart body (init + resize + dispose mirrors the TrendRenderer
 * pattern). Around it we render:
 *   - Sum-of-source flow as a headline number with `powerUnit` (default 'W')
 *   - COP badge coloured by band (>4 green, 3-4 amber, <3 grey)
 *   - Empty state ("Kein Energiefluss") when every link is null/zero
 *
 * Staleness rule: nodes whose incoming/outgoing links have bad-quality
 * ValuePoints get 50% alpha in the ECharts option (per-node itemStyle).
 */

import { useEffect, useMemo, useRef } from 'preact/hooks';
import * as echarts from 'echarts';
import type { ECharts, EChartsOption } from 'echarts';
import type { EnergyFlowWidget } from '../widgets';
import type { IPointBindingSource, PointValue } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { formatNumber } from '../../format/leafValue';
import './EnergyFlowRenderer.css';

interface Props {
  widget: EnergyFlowWidget;
  source: IPointBindingSource;
}

const DEFAULT_COLOUR = '#3b82f6';

export function EnergyFlowRenderer({ widget, source }: Props) {
  // One pointSignal per link.valuePoint (thickness driver). Optional COP
  // signal for the overlay badge. Kept stable per widget instance via useMemo
  // + explicit release() on unmount, mirroring SchematicOverlayRenderer.
  const linkSignals = useMemo(
    () => widget.links.map((link) => pointSignal(source, link.valuePoint)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widget.id, widget.links.length, ...widget.links.map((l) => l.valuePoint)],
  );
  const copSignal = useMemo(
    () => (widget.copPoint ? pointSignal(source, widget.copPoint) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widget.id, widget.copPoint],
  );
  useEffect(
    () => () => {
      for (const s of linkSignals) s.release();
      copSignal?.release();
    },
    [linkSignals, copSignal],
  );

  // Read each signal's value (Preact tracks the .value access).
  const linkValues = linkSignals.map((s) => s.value.value);
  const copValue = copSignal?.value.value ?? null;

  const unit = widget.powerUnit ?? 'W';

  // Sum-of-source: pick the "root" nodes (no incoming link) and sum the
  // outgoing link flows. That is the operator's headline "how much energy
  // enters the system right now".
  const inbound = new Set<string>();
  for (const l of widget.links) inbound.add(l.toNodeId);
  const sourceNodeIds = widget.nodes
    .filter((n) => !inbound.has(n.id))
    .map((n) => n.id);
  let totalSourceFlow = 0;
  let sawAny = false;
  widget.links.forEach((link, idx) => {
    if (!sourceNodeIds.includes(link.fromNodeId)) return;
    const v = coerceNumber(linkValues[idx]?.value);
    if (v != null && v > 0) {
      totalSourceFlow += v;
      sawAny = true;
    }
  });

  const copNum = coerceNumber(copValue?.value);
  const copBand = classifyCop(copNum);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);

  // Mount / dispose ECharts instance — same lifecycle as TrendRenderer.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const inst = echarts.init(el);
    chartRef.current = inst;
    const ro = new ResizeObserver(() => inst.resize());
    ro.observe(el);
    return () => {
      ro.disconnect();
      inst.dispose();
      chartRef.current = null;
    };
  }, []);

  // Push option updates on every signal tick.
  useEffect(() => {
    const inst = chartRef.current;
    if (!inst) return;
    inst.setOption(
      buildSankeyOption(widget, linkValues, unit),
      { notMerge: true },
    );
  }, [widget, linkValues, unit]);

  return (
    <div class="hv-energy-flow">
      <header class="hv-energy-flow__head">
        {widget.label && <div class="hv-energy-flow__label">{widget.label}</div>}
        <div class="hv-energy-flow__total">
          <span class="hv-energy-flow__total-value">
            {sawAny ? formatPower(totalSourceFlow) : '—'}
          </span>
          <span class="hv-energy-flow__total-unit">{unit}</span>
        </div>
        {copNum != null && (
          <div class={`hv-energy-flow__cop hv-energy-flow__cop--${copBand}`}>
            <span class="hv-energy-flow__cop-value">{formatNumber(copNum, 2)}</span>
            <span class="hv-energy-flow__cop-label">COP</span>
          </div>
        )}
      </header>
      <div class="hv-energy-flow__chart-wrap">
        <div ref={containerRef} class="hv-energy-flow__chart" />
        {!sawAny && (
          <div class="hv-energy-flow__empty">Kein Energiefluss</div>
        )}
      </div>
    </div>
  );
}

// ---------- ECharts option builder ----------

function buildSankeyOption(
  widget: EnergyFlowWidget,
  linkValues: (PointValue | null)[],
  unit: string,
): EChartsOption {
  // Track which nodes touch a bad-quality link so we can dim them.
  const staleNodes = new Set<string>();
  widget.links.forEach((link, idx) => {
    const pv = linkValues[idx];
    if (isDegraded(pv)) {
      staleNodes.add(link.fromNodeId);
      staleNodes.add(link.toNodeId);
    }
  });

  const nodes = widget.nodes.map((n) => {
    const stale = staleNodes.has(n.id);
    const colour = n.colour ?? DEFAULT_COLOUR;
    return {
      name: n.id,
      // ECharts renders the label from `name` unless we override it below.
      itemStyle: {
        color: colour,
        opacity: stale ? 0.5 : 1,
        borderColor: 'transparent',
      },
      label: {
        formatter: () => n.label,
        color: stale ? 'rgba(200,200,200,0.7)' : undefined,
      },
    };
  });

  const links = widget.links
    .map((link, idx) => {
      const pv = linkValues[idx];
      const raw = coerceNumber(pv?.value);
      // Sankey ignores non-positive values; clamp to a tiny epsilon so the
      // link stays visible while zero without collapsing the layout.
      const value = raw != null && raw > 0 ? raw : 0.001;
      const stale = isDegraded(pv);
      return {
        source: link.fromNodeId,
        target: link.toNodeId,
        value,
        lineStyle: {
          opacity: stale ? 0.25 : 0.55,
          curveness: 0.5,
        },
        emphasis: { lineStyle: { opacity: 0.75 } },
      };
    });

  return {
    animation: false,
    tooltip: {
      trigger: 'item',
      formatter: (p: unknown) => {
        const params = p as {
          dataType?: 'node' | 'edge';
          data?: { source?: string; target?: string; value?: number; name?: string };
        };
        if (params?.dataType === 'edge' && params.data) {
          const raw = params.data.value ?? 0;
          const shown = raw <= 0.001 ? '0' : formatPower(raw);
          return `${params.data.source} → ${params.data.target}<br/><b>${shown}</b> ${unit}`;
        }
        if (params?.dataType === 'node' && params.data) {
          const n = widget.nodes.find((x) => x.id === params.data?.name);
          return n?.label ?? params.data.name ?? '';
        }
        return '';
      },
    },
    series: [
      {
        type: 'sankey',
        left: 8,
        right: 8,
        top: 8,
        bottom: 8,
        nodeAlign: 'justify',
        nodeGap: 12,
        nodeWidth: 14,
        emphasis: { focus: 'adjacency' },
        data: nodes,
        links,
        label: {
          color: 'var(--text-primary)',
          fontSize: 11,
        },
      },
    ],
  };
}

// ---------- helpers ----------

function classifyCop(cop: number | null): 'good' | 'warn' | 'poor' | 'none' {
  if (cop == null) return 'none';
  if (cop > 4) return 'good';
  if (cop >= 3) return 'warn';
  return 'poor';
}

function coerceNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  return null;
}

/** Compact human-friendly power formatter. Keeps the headline number
 *  short — 12.4 kW instead of 12384. Unit is rendered separately by the
 *  caller, so we return the numeric part only. */
function formatPower(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e6) return formatNumber(n / 1e6, 2);
  if (abs >= 1e3) return formatNumber(n / 1e3, 2);
  if (abs >= 10) return formatNumber(n, 0);
  return formatNumber(n, 1);
}
